import { useEffect, useRef, useState } from "react";
import { X, Send, Loader2 } from "lucide-react";
import { startChat, sendChat } from "../lib/chatApi";
import { trackCtaClick } from "../lib/api";

// Pink-themed WhatsApp-style chat widget for /pink visitors.
// Design constraints (per user):
//   • Small sticky icon (compact, not a giant button).
//   • Positioned AWAY from every CTA: anchored at top-left mid-screen so it
//     never overlaps with hero-buy-btn / pink-sticky-cta / final-buy-btn
//     (those all sit on the right or bottom).
//   • All replies come from page content rules (chat_router._PINK_RULES).
//   • Every CTA link inside chat is a plain https://bluebook.cx/pink link
//     so clicks are trackable via `chat-link-checkout-pink`.
//   • Backend persists each session with funnel='pink' so the Admin
//     Dashboard can show "Conversations started with WhatsApp bot" and
//     "Checkouts came from chat" separately for the Pink segment.

const STORAGE_KEY = "bb_chat_session_pink_v1";

const PINK = {
  primary: "#8D6E79",  // mauve (matches Pink landing CTAs)
  dark: "#3E2F32",
  bg: "#FBF5F1",       // ivory chat wallpaper
  bubbleOut: "#F3E6E8",
  bubbleIn: "#FFFFFF",
  accent: "#C8A977",   // gold
  badge: "#D8B7BE",    // soft pink
};

function WhatsAppGlyph({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
      <path d="M16.001 3.2C9.046 3.2 3.4 8.846 3.4 15.801c0 2.276.605 4.486 1.752 6.422L3.2 28.8l6.756-1.917a12.6 12.6 0 0 0 6.045 1.54h.005c6.95 0 12.6-5.646 12.6-12.6 0-3.367-1.31-6.531-3.69-8.91A12.522 12.522 0 0 0 16.001 3.2Zm5.745 15.91c-.314-.158-1.86-.918-2.149-1.023-.289-.105-.499-.158-.708.158-.21.316-.812 1.022-.995 1.232-.183.21-.367.236-.681.079-.314-.158-1.327-.49-2.527-1.56-.934-.832-1.564-1.86-1.748-2.176-.183-.316-.02-.486.138-.643.142-.141.314-.367.472-.55.158-.184.21-.316.314-.526.105-.21.052-.394-.026-.552-.078-.157-.708-1.706-.97-2.337-.255-.613-.515-.53-.708-.54l-.604-.011a1.158 1.158 0 0 0-.84.394c-.288.316-1.102 1.077-1.102 2.626 0 1.549 1.128 3.046 1.286 3.255.157.21 2.22 3.39 5.378 4.755.751.325 1.337.518 1.793.663.753.24 1.439.206 1.98.125.604-.09 1.86-.76 2.122-1.494.262-.735.262-1.366.183-1.494-.078-.13-.288-.21-.602-.367Z"/>
    </svg>
  );
}

function formatMessageText(text, onLinkClick) {
  const parts = String(text || "").split(/(https?:\/\/[^\s]+)/g);
  return parts.map((p, i) =>
    /^https?:\/\//.test(p) ? (
      <a
        key={i}
        href={p}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onLinkClick?.(p)}
        data-testid="pink-chat-bot-link"
        className="underline decoration-2 underline-offset-2 break-all"
        style={{ color: PINK.primary }}
      >
        {p}
      </a>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

export default function ChatWidgetPink() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [booting, setBooting] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.session_id && Array.isArray(parsed?.messages)) {
          setSessionId(parsed.session_id);
          setMessages(parsed.messages);
        }
      }
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ session_id: sessionId, messages }));
    } catch (_) { /* ignore */ }
  }, [sessionId, messages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const openChat = async () => {
    trackCtaClick("pink-chat-open", "landing-pink");
    setOpen(true);
    if (!sessionId) {
      setBooting(true);
      try {
        const r = await startChat("pink");
        setSessionId(r.session_id);
        setMessages([{ role: "bot", text: r.greeting, ts: Date.now() }]);
      } catch (e) {
        setMessages([{ role: "bot", text: "أهلاً بكِ 🌸 الخدمة بسيطة الحين، حاولي لاحقاً أو راسلينا على support@bluebook.cx", ts: Date.now() }]);
      } finally {
        setBooting(false);
        setTimeout(() => inputRef.current?.focus(), 200);
      }
    } else {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending || !sessionId) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text, ts: Date.now() }]);
    setSending(true);
    trackCtaClick("pink-chat-message-sent", "landing-pink");
    try {
      const r = await sendChat(sessionId, text);
      setMessages((prev) => [...prev, { role: "bot", text: r.reply, ts: Date.now() }]);
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: "bot",
        text: "عذراً، حصل خلل بسيط. حاولي مرة ثانية أو راسلينا support@bluebook.cx",
        ts: Date.now(),
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleLinkClick = (url) => {
    let label = "pink-chat-link-other";
    if (/\/pink/.test(url)) label = "pink-chat-link-checkout";  // tracks checkout-from-chat for Pink
    else if (/\/sample/.test(url)) label = "pink-chat-link-sample";
    else if (/\/refund/.test(url)) label = "pink-chat-link-refund";
    trackCtaClick(label, "landing-pink");
  };

  return (
    <>
      {/* Floating launcher — SMALL, anchored to LEFT side (mid-bottom) so it
          never overlaps the right-side CTAs: hero-buy-btn (top-right),
          pink-sticky-cta (bottom full-width), final-buy-btn (mid). */}
      {!open && (
        <button
          type="button"
          onClick={openChat}
          data-testid="pink-chat-launcher"
          aria-label="تحدثي معنا"
          className="fixed left-3 bottom-28 sm:bottom-6 z-[70] flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-[0_10px_25px_rgba(141,110,121,0.45)] hover:shadow-[0_14px_35px_rgba(141,110,121,0.6)] transition-all hover:scale-[1.06]"
          style={{ backgroundColor: PINK.primary, color: "#fff" }}
        >
          <WhatsAppGlyph className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          {/* tiny attention dot */}
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white animate-pulse" style={{ backgroundColor: PINK.accent }} />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed inset-x-3 bottom-3 sm:bottom-6 sm:left-5 sm:right-auto sm:w-[360px] sm:h-[540px] h-[72vh] z-[100] rounded-2xl overflow-hidden shadow-[0_30px_70px_rgba(62,47,50,0.45)] flex flex-col"
          data-testid="pink-chat-panel"
          style={{ backgroundColor: PINK.bg }}
          dir="rtl"
        >
          {/* Header */}
          <div
            className="flex items-center gap-3 px-3 py-2.5 text-white"
            style={{ backgroundColor: PINK.primary }}
          >
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-xs font-black shrink-0" style={{ color: PINK.primary }}>
              ك.و
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold leading-tight">تحدثي معنا</div>
              <div className="text-[11px] opacity-90 leading-tight flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: PINK.accent }} />
                متصلة الآن · الكتاب الوردي
              </div>
            </div>
            <button
              type="button"
              onClick={() => { setOpen(false); trackCtaClick("pink-chat-close", "landing-pink"); }}
              className="p-1.5 rounded-full hover:bg-white/15 transition-colors"
              aria-label="إغلاق"
              data-testid="pink-chat-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-3 space-y-2"
            style={{ backgroundColor: PINK.bg }}
            data-testid="pink-chat-messages"
          >
            {booting && (
              <div className="flex items-center gap-2 text-xs" style={{ color: PINK.primary }}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> جاري التحضير...
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "ml-auto" : "mr-auto"}`}
                style={{
                  backgroundColor: m.role === "user" ? PINK.bubbleOut : PINK.bubbleIn,
                  color: PINK.dark,
                  borderTopRightRadius: m.role === "user" ? 4 : undefined,
                  borderTopLeftRadius: m.role === "bot" ? 4 : undefined,
                  boxShadow: "0 1px 2px rgba(62,47,50,0.08)",
                }}
                data-testid={`pink-chat-msg-${m.role}`}
              >
                {formatMessageText(m.text, handleLinkClick)}
              </div>
            ))}
            {sending && (
              <div className="mr-auto max-w-[60%] px-3 py-2 rounded-2xl text-xs flex items-center gap-1.5" style={{ backgroundColor: PINK.bubbleIn, color: PINK.primary }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: PINK.primary }} />
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: PINK.primary, animationDelay: "0.15s" }} />
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: PINK.primary, animationDelay: "0.3s" }} />
              </div>
            )}
          </div>

          {/* Suggestion chips — first-time user */}
          {messages.length <= 1 && !booting && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5" data-testid="pink-chat-suggestions">
              {["كم سعره؟", "وش يحتوي البرنامج؟", "الجلسة الأولى مجاناً", "الضمان والإرجاع"].map((q) => (
                <button
                  key={q}
                  onClick={() => { setInput(q); setTimeout(() => handleSend(), 50); }}
                  className="text-[11px] px-2.5 py-1 rounded-full border transition-colors"
                  style={{ borderColor: PINK.badge, color: PINK.primary, backgroundColor: "#fff" }}
                  data-testid="pink-chat-suggestion-chip"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input bar */}
          <form
            onSubmit={handleSend}
            className="flex items-center gap-2 px-2.5 py-2 border-t"
            style={{ backgroundColor: "#fff", borderColor: PINK.badge }}
          >
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="اكتبي سؤالكِ هنا..."
              className="flex-1 px-3 py-2 text-sm rounded-full bg-[#F3E6E8]/40 focus:outline-none focus:ring-2"
              style={{ color: PINK.dark, "--tw-ring-color": PINK.primary }}
              maxLength={500}
              data-testid="pink-chat-input"
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              className="w-9 h-9 rounded-full flex items-center justify-center disabled:opacity-40 transition-transform hover:scale-105"
              style={{ backgroundColor: PINK.primary, color: "#fff" }}
              data-testid="pink-chat-send-btn"
              aria-label="إرسال"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
